import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Link,
  SegmentedControl,
  StatusIndicator,
  Icon,
  Spinner,
  Popover,
  ProgressBar,
  Modal,
} from '@cloudscape-design/components';

import { ViewLayout } from '../../../components/ViewLayout';
import { useEffect, useMemo, useState } from 'react';
import {
  LoadedPlaceReport,
  LoadedPlaceReportPublic,
  PlaceBasic,
  PlacePeriodReport,
  PlaceReport,
  PlaceReportPublic,
  REPORT_CARD_TASKS,
  StatisticStatus,
  Task,
  TASK_NAME_INFO_MAP,
} from '../../../common/types';
import { StatisticBox } from '../../../components/StatisticBox';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReport, getReportPublic } from '../../../common/api/reports';
import { LoadingOrError } from '../../../components/Loading';
import ReportCardHeader from '../../../components/ReportCardHeader';
import { ReviewList } from '../../../components/ReviewList';
import { TASK_NAME_MAP } from '../../../common/types';
import { useQueryParam } from 'use-query-params';
import { withDefault } from 'use-query-params';
import { StringParam } from 'use-query-params';
import { ScorecardHeader } from '../../../components/ScorecardHeader';
import { CardTaskProgressView } from '../../../components/CardTaskProgressView';

export default function View() {
  const { public_code } = useParams();

  const getQuery = useQuery({
    queryKey: ['reports', public_code],
    queryFn: () => getReportPublic(public_code ?? ''),
    enabled: !!public_code,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return (
    <ViewLayout
      header={<ReportCardHeader />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      {getQuery.isLoading || !getQuery.data || getQuery.error || !public_code ? (
        <LoadingOrError
          loading={getQuery.isLoading}
          retry={() => getQuery.refetch()}
          error={getQuery.error}
        />
      ) : (
        <CardTaskView report={getQuery.data} public_code={public_code} />
      )}
    </ViewLayout>
  );
}

export type CardTaskViewProps = {
  report: PlaceReportPublic;
  public_code: string;
};

export function CardTaskView(props: CardTaskViewProps) {
  const [report, setReport] = useState(props.report);
  const getReq = useMutation({
    mutationFn: () => getReportPublic(props.public_code),
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (JSON.stringify(report) !== JSON.stringify(data)) {
        setReport(data);
      }
    },
  });

  const currentTask = useMemo(() => {
    if (report.tasks.length === 0) {
      return undefined;
    }
    return [...report.tasks]
      .filter((task) => REPORT_CARD_TASKS.includes(task.name))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  }, [report.tasks]);

  const status = useMemo(() => {
    if (report.periods && report.periods.length > 0) {
      return 'success';
    }
    if (!currentTask) {
      return 'in-progress';
    }
    if (currentTask.status === 'pending') {
      return 'in-progress';
    }
    if (currentTask.status === 'in-progress') {
      return 'in-progress';
    }
    // check for specific task name
    if (currentTask.status === 'completed') {
      if (currentTask.name === 'report_statistics') {
        return 'success';
      }
      return 'in-progress';
    }
    if (currentTask.status === 'failed' && currentTask.retries && currentTask.retries >= 3) {
      return 'error';
    }
    return 'in-progress';
  }, [currentTask]);

  useEffect(() => {
    const pollInterval = setInterval(async () => {
      // TODO call poll function better to look for changes
      if (status === 'in-progress') {
        await getReq.mutateAsync();
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [getReq, report, status]);

  if (status === 'success' && report.place !== undefined) {
    // TODO show message to check email or text for report card
    return <CompletedCardView report={report as LoadedPlaceReportPublic} />;
  }

  return <CardTaskProgressView place={report.place} currentTask={currentTask} status={status} />;
}

export type CompletedCardViewProps = {
  report: LoadedPlaceReportPublic;
};

const RangeFiltersParam = withDefault(StringParam, '180-days');

export function CompletedCardView(props: CompletedCardViewProps) {
  const report = props.report;
  const placeName = report.place.name;
  const placeAddress = report.place.address;

  // TODO change to use report.report_at?
  const createdAt = new Date(report.report_at + 'Z');
  // TODO make sure this format looks right
  // const rangeEnd = "1/28/2025";
  const rangeEnd = createdAt.toLocaleDateString();

  const [selectedRange, setSelectedRange] = useQueryParam('range', RangeFiltersParam);
  const rangeOptions = [
    { text: '30 Days', id: '30-days' },
    { text: '60 Days', id: '60-days' },
    { text: '90 Days', id: '90-days' },
    { text: '180 Days', id: '180-days' },
  ];

  const currentRange = rangeOptions.find((option) => option.id === selectedRange)!;
  const currentRangeIdx = useMemo(() => {
    return report.periods.findIndex((period) => period.range === selectedRange);
  }, [report.periods, selectedRange]);

  const period = report.periods[currentRangeIdx];
  const placeLetterGrade = period.place_letter_grade;
  const placePercentile = period.place_percentile;
  const borderColor = useMemo(() => {
    const grade = period.place_letter_grade;
    if (grade.includes('A')) {
      return 'border-green-600';
    } else if (grade.includes('B')) {
      return 'border-green-400';
    } else if (grade.includes('C')) {
      return 'border-yellow-400';
    }
    // D and F are both red
    return 'border-red-500';
  }, [period.place_letter_grade]);

  return (
    <Grid
      gridDefinition={[
        {
          colspan: {
            default: 12,
          },
        },
        {
          colspan: {
            default: 12,
          },
        },
      ]}
    >
      <Container
        header={
          <Header variant="h1" description={`Created ${rangeEnd}`}>
            Reputation Scorecard
          </Header>
        }
        footer={
          <SegmentedControl
            selectedId={selectedRange}
            onChange={({ detail }) => setSelectedRange(detail.selectedId)}
            label="View past"
            options={rangeOptions}
          />
        }
      >
        <ScorecardHeader
          placeLetterGrade={placeLetterGrade}
          placePercentile={placePercentile}
          place={report.place}
          borderColor={borderColor}
        />
      </Container>
      {/* underline the text */}
      <Box variant="div" textAlign="center" className="p-4">
        <Box variant="p" textAlign="center" fontSize="heading-l" color="text-status-info">
          <Icon name="status-info" variant="subtle" size="inherit" /> We sent you a text and an
          email with a link to your comprehensive Google Reputation Score Card!
        </Box>
      </Box>
    </Grid>
  );
}
